import React from 'react';
import { ShieldCheck, Sparkles } from 'lucide-react';
import WazinWatermark from '../WazinWatermark';
import { useLaborPortalLanguage } from '../../features/labor-intake/LaborPortalLanguageContext';

const LaborStandaloneLayout = ({ children, footerNote, badgeText = null }) => {
  const { language, setLanguage, languages, isRtl, text } = useLaborPortalLanguage();
  const fallbackBadgeText = text('بوابة وازن العمالية', 'Wazin Labor Portal');
  const portalFontFamily = language === 'ml'
    ? '"Noto Sans Malayalam", "Nirmala UI", "Kartika", sans-serif'
    : undefined;

  return (
    <div
      className="min-h-screen bg-[#f8f6f1]"
      dir={isRtl ? 'rtl' : 'ltr'}
      lang={language}
      style={portalFontFamily ? { fontFamily: portalFontFamily } : undefined}
    >
      <WazinWatermark opacity={0.08} size="large" position="center" />

      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="sticky top-0 z-40 border-b border-[#C8A870]/15 bg-white/90 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
            <nav
              aria-label={text('اختيار لغة بوابة العميل', 'Select client portal language')}
              className="flex w-full flex-nowrap gap-2.5 overflow-x-auto rounded-2xl border border-[#C8A870]/18 bg-white/95 p-2.5 shadow-[0_8px_28px_rgba(31,59,100,0.07)] [scrollbar-width:none] lg:justify-center [&::-webkit-scrollbar]:hidden"
              dir="ltr"
            >
              {languages.map((item, index) => {
                const active = language === item.code;
                return (
                  <React.Fragment key={item.code}>
                    {index > 0 ? <span aria-hidden="true" className="my-2 hidden h-7 w-px shrink-0 bg-[#C8A870]/25 sm:block" /> : null}
                    <button
                      type="button"
                      onClick={() => setLanguage(item.code)}
                      aria-pressed={active}
                      className={`flex min-h-11 shrink-0 items-center gap-2.5 rounded-xl border px-4 py-2 text-sm font-bold transition-all duration-200 ${
                        active
                          ? 'border-[#A27849] bg-gradient-to-b from-[#B58A55] to-[#967043] text-white shadow-[0_6px_16px_rgba(162,120,73,0.24)]'
                          : 'border-transparent bg-white text-[#1F3B64] hover:-translate-y-0.5 hover:border-[#C8A870]/25 hover:bg-[#fcfaf6] hover:shadow-[0_6px_16px_rgba(31,59,100,0.07)]'
                      }`}
                    >
                      <span className="inline-flex h-6 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[5px] border border-black/5 bg-white shadow-[0_1px_4px_rgba(15,23,42,0.12)]">
                        <img src={item.flag} alt={item.flagAlt} className="h-full w-full object-cover" />
                      </span>
                      <span className="whitespace-nowrap">{item.label}</span>
                    </button>
                  </React.Fragment>
                );
              })}
            </nav>

            <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <img
                    src="/images/wazin-logo.png"
                    alt="Wazin Logo"
                    className="h-20 w-auto object-contain sm:h-24 lg:h-28"
                  />
                  <span className="hidden rounded-full bg-[#A27849]/10 px-3 py-1 text-xs font-bold text-[#A27849] sm:inline-flex">
                    {badgeText || fallbackBadgeText}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#C8A870]/25 bg-[#f8f6f1] px-3 py-1.5 text-xs font-semibold text-[#1F3B64]">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#A27849]" />
                    {text('بوابة القضايا العمالية', 'Labor Cases Portal')}
                  </span>
                  <span className="inline-flex rounded-full border border-[#C8A870]/20 bg-white px-3 py-1.5 text-xs text-slate-500">
                    {text('صفحة مخصصة لتقديم البيانات والمستندات', 'A page dedicated to submitting information and documents')}
                  </span>
                </div>
              </div>
            </div>
            </div>
          </div>
        </header>

        <div className="flex-1">{children}</div>

        <footer className="border-t border-[#C8A870]/15 bg-white/80">
          <div className="mx-auto max-w-7xl px-4 py-6 text-center sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1F3B64]/5 px-4 py-2 text-sm text-slate-600">
              <Sparkles className="h-4 w-4 text-[#A27849]" />
              {footerNote || text('منصة وازن قيد الإطلاق التدريجي.', 'Wazin platform is being launched gradually.')}
            </div>
            <p className="mt-3 text-xs text-slate-400">
              © 2026 Wazin Law Firm
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LaborStandaloneLayout;
